import React, { useCallback, useMemo } from "react";
import { FlatList } from "react-native";
import { PreloadedQuery, usePreloadedQuery, graphql } from "react-relay";

import { WidgetNote } from "widgets";
import { WACategoryWidgetContainer } from "../../WACategoryWidgetContainer";
import { WidgetSizeT } from "../../../localTypes/WidgetSizeT";
import { keyExtractorId } from "../../utils/keyExtractors";
import { noteWidgetSizes } from "../../utils/widgetSizes/noteWidgetSizes";
import { useWACategoryLayout } from "../../utils/useWACategoryLayout";
import { getWidgetAddWidgetSizeWithinBounds } from "../../utils/useWidgetAddWidgetSizeState";
import { WACategoryNotePreviewsQuery } from "./__generated__/WACategoryNotePreviewsQuery.graphql";
import { WACategoryItemSeparator } from "../../WACategoryItemSeparator";
import { WANotePreviewTemplate } from "./WANotePreviewTemplate";
import { FormattedMessage, useIntl } from "react-intl";
import { WACategoryHeaderAdminButton } from "../../WACategoryAdminButtons";

export const WA_CATEGORY_NOTE_PREVIEWS_QUERY = graphql`
  query WACategoryNotePreviewsQuery(
    $storecastId: ID!
    $input1: PreviewWidgetNoteInput!
    $input2: PreviewWidgetNoteInput!
    $input3: PreviewWidgetNoteInput!
  ) @raw_response_type {
    dashboard(id: $storecastId) @required(action: THROW) {
      previewWidgetNote1: previewWidgetNote(input: $input1) {
        id
        title
        ...WidgetNote_widgetNote
      }
      previewWidgetNote2: previewWidgetNote(input: $input2) {
        id
        title
        ...WidgetNote_widgetNote
      }
      previewWidgetNote3: previewWidgetNote(input: $input3) {
        id
        title
        ...WidgetNote_widgetNote
      }
    }
  }
`;

type Props = {
  queryRef: PreloadedQuery<WACategoryNotePreviewsQuery>;
  unitSize: number;
  maxSize: WidgetSizeT;
  onAdd(_: { title: string; template: WANotePreviewTemplate }): void;
  onAddExisting?(): void;
};

export function WACategoryNotePreviews(props: Props) {
  const data = usePreloadedQuery<WACategoryNotePreviewsQuery>(
    WA_CATEGORY_NOTE_PREVIEWS_QUERY,
    props.queryRef
  );
  const contentContainerStyle = useWACategoryLayout();

  const listData = useMemo(
    () => [
      data.dashboard.previewWidgetNote1,
      data.dashboard.previewWidgetNote2,
      data.dashboard.previewWidgetNote3,
    ],
    [
      data.dashboard.previewWidgetNote1,
      data.dashboard.previewWidgetNote2,
      data.dashboard.previewWidgetNote3,
    ]
  );

  const intl = useIntl();

  const empty = useMemo(
    () =>
      intl.formatMessage({
        description: "WACategoryNotePreview template description empty",
        defaultMessage: "Empty widget",
      }),
    [intl]
  );
  const descriptions = useMemo(
    () => [
      "",
      intl.formatMessage({
        description:
          "WACategoryNotePreview template description image and text",
        defaultMessage: "Highlight good examples with image and text",
      }),
      intl.formatMessage({
        description: "WACategoryNotePreview template description slideshow",
        defaultMessage: "Create a slideshow with multiple images",
      }),
    ],
    [intl]
  );

  const [size, hasSizeWithinBounds] = useMemo(
    () =>
      getWidgetAddWidgetSizeWithinBounds({
        widgetSizes: noteWidgetSizes,
        maxSize: props.maxSize,
      }),
    [props.maxSize]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      return (
        <WACategoryWidgetContainer
          key={item.id}
          hasSizeWithinBounds={hasSizeWithinBounds}
          size={size}
          description={descriptions[index]}
          title={item.title === "" ? empty : item.title}
          unitSize={props.unitSize}
          onAdd={() =>
            props.onAdd({
              title: item.title,
              template:
                index === 0
                  ? "empty"
                  : index === 1
                    ? "dailyPositive"
                    : "slideshow",
            })
          }
        >
          <WidgetNote
            mode="IDLE"
            widget={item}
            interactive={false}
            idiom="STATIC"
          />
        </WACategoryWidgetContainer>
      );
    },
    [descriptions, empty, hasSizeWithinBounds, props, size]
  );

  return (
    <FlatList
      data={listData}
      keyExtractor={keyExtractorId}
      contentContainerStyle={contentContainerStyle}
      renderItem={renderItem}
      ItemSeparatorComponent={WACategoryItemSeparator}
      ListHeaderComponent={
        props.maxSize.w >= 2 && props.onAddExisting ? (
          <WACategoryHeaderAdminButton
            title={
              <FormattedMessage
                defaultMessage="Add Existing"
                description="WidgetNote add existing note button"
              />
            }
            onPress={() => {
              props.onAddExisting?.();
            }}
          />
        ) : null
      }
    />
  );
}
